import TableMui from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { PAGE_INITIAL_VALUE, ROWS_PER_PAGE_OPTION } from 'common/constants';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { formatCNPJ } from 'utils/formatDate';
import { formatPhoneWithAreaCode } from 'utils/formatters';
import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { showSnackbar } from 'v3/data/store/snackbar/actions';
import { setAllStoreList, setFilterCurrentPage, setFilterRowsPerPage } from 'v3/data/store/stores/actions';
import { IStoresListItem } from 'v3/data/store/stores/types';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import * as api from 'v3/services/api';
import { getAllStores, getFlagStatus } from 'v3/services/api';
import { StoresMobileTable } from 'v3/shared/components/MobileTable/StoresMobileTable';
import { NoItemsFound } from 'v3/shared/components/NoItemsFound';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';
import { SubtitleMedium } from 'v3/shared/typography/SubtitleMedium';
import { theme } from 'v3/styles/global-styles';
import { EditIcon, PaperStyled, TableCellGroup, TableCellStyled, TablePaginationStyles, TableRowName } from '../styles';
import { EditStoreNameAndGroupModal } from 'v3/shared/components/Modal/EditStoreNameAndGroupModal';
import { IEditStoreNameAndGroupData } from 'v3/shared/components/Modal/EditStoreNameAndGroupModal/types';

export const StoresTable = () => {
  const { count, items } = useStoreSelector((store) => store.store.stores);
  const { profileId, storeUserId } = useStoreSelector((store) => store.store.storeLogged);
  const { rowsPerPage, page, searchStore } = useStoreSelector((store) => store.store.storesFilter);
  const [storeGroupRefactorFlag, setStoreGroupRefactorFlag] = useState<Boolean>(false);

  const { isDesktop } = useBreakpoints();
  const [openModal, setOpenModal] = useState(false);
  const [selectedStore, setSelectedStore] = useState<IStoresListItem>();

  const fetchStoreList = useCallback(async () => {
    const flagResponse = await getFlagStatus(FeatureFlagEnum.IKB2C_4692_STORE_GROUPS_IMPROVEMENT);
    setStoreGroupRefactorFlag(flagResponse.data.enabled);
    try {
      const response = await getAllStores({
        storeUserId,
        profileId,
        searchStore,
        rowsPerPage,
        page,
      });

      setAllStoreList(response.data);
    } catch (error) {
      showErrorDialog('Erro ao obter lista de lojas', error);
    }
  }, [profileId, searchStore, rowsPerPage, page]);

  useEffect(() => {
    //TODO: remove this when store refactor flag is approved
    (profileId || storeUserId) && fetchStoreList();
  }, [fetchStoreList, profileId, storeUserId]);

  const handleOpenModal = (store: IStoresListItem) => {
    setSelectedStore(store);
    setOpenModal(true);
  };

  const handleSaveStoreNameAndGroup = async (newStoreNameAndGroup: IEditStoreNameAndGroupData) => {
     try {
       if (!selectedStore) return;
 
       await api.updateStoreNameAndGroup(selectedStore.storeUserId, { name: newStoreNameAndGroup.name, storeGroupId: newStoreNameAndGroup.storeGroupId });

       fetchStoreList()
       showSnackbar('Loja alterada com sucesso', 'success', true);
       setOpenModal(false);
     } catch (error) {
       showErrorDialog('Erro ao atualizar loja', error);
     }
   };

  return (
    <Fragment>
      {items.isEmpty ? (
        <NoItemsFound />
      ) : isDesktop ? (
        <PaperStyled>
          <TableContainer sx={{ maxHeight: 450 }}>
            <TableMui stickyHeader aria-label="sticky tables">
              <TableHead>
                <TableRow>
                  <TableCellStyled>Nome</TableCellStyled>
                  {storeGroupRefactorFlag && <TableCellStyled>Grupo</TableCellStyled>}
                  <TableCellStyled>Contato</TableCellStyled>
                </TableRow>
              </TableHead>

              <TableBody>
                {items.map((store, index) => (
                  <TableRow key={store.storeUserId}>
                    <TableCellGroup key={`name${index}`}>
                      <TableRowName>
                        <SubtitleMedium>{store.name}</SubtitleMedium>
                        <EditIcon
                          onClick={() => {
                            handleOpenModal(store);
                          }}
                        />
                      </TableRowName>
                      <SubtitleMedium color={theme.palette.secondary.main}>{formatCNPJ(store.cnpj)}</SubtitleMedium>
                    </TableCellGroup>

                    {storeGroupRefactorFlag && (
                      <TableCellGroup key={`group${index}`}>
                        <SubtitleMedium>{store.storeGroup?.name ?? '-'}</SubtitleMedium>
                      </TableCellGroup>
                    )}

                    <TableCellGroup key={`contact-${index}`}>
                      <SubtitleMedium>{store.email}</SubtitleMedium>
                      <SubtitleMedium color={theme.palette.secondary.main}>
                        {store.phone?.areaCode && store.phone?.phoneNumber
                          ? formatPhoneWithAreaCode(store.phone.areaCode, store.phone.phoneNumber)
                          : null}
                      </SubtitleMedium>
                    </TableCellGroup>
                  </TableRow>
                ))}
              </TableBody>
            </TableMui>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTION}
            component="div"
            sx={TablePaginationStyles}
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, value) => {
              setFilterCurrentPage(value);
            }}
            onRowsPerPageChange={(event) => {
              setFilterCurrentPage(PAGE_INITIAL_VALUE);
              setFilterRowsPerPage(Number(event.target.value));
            }}
          />
        </PaperStyled>
      ) : (
        <StoresMobileTable storesList={items} featureFlag={storeGroupRefactorFlag} />
      )}

{selectedStore && (
  <EditStoreNameAndGroupModal
    title="Editar loja"
    active={openModal}
    onClose={() => setOpenModal(false)}
    onSave={handleSaveStoreNameAndGroup}
    currentStore={selectedStore}
  />
)}
    </Fragment>
  );
};
